import { useEffect, useRef } from 'react';

interface UseSwipeDrawerOptions {
  onOpen: () => void;
  onClose: () => void;
  isOpen: boolean;
  enabled: boolean;
  edgeThreshold?: number;
  minSwipeDistance?: number;
}

/**
 * Обрабатывает touch-свайпы для открытия/закрытия drawer.
 * - Свайп вправо из левого края экрана (< edgeThreshold px) -> open
 * - Свайп влево при открытом drawer -> close
 * - Не мешает вертикальному скроллу
 */
export function useSwipeDrawer({
  onOpen,
  onClose,
  isOpen,
  enabled,
  edgeThreshold = 24,
  minSwipeDistance = 50,
}: UseSwipeDrawerOptions): void {
  const startX = useRef(0);
  const startY = useRef(0);
  const isTracking = useRef(false);
  const isOpenRef = useRef(isOpen);

  // Sync ref without re-subscribing listeners
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!enabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      isTracking.current = isOpenRef.current || touch.clientX < edgeThreshold;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isTracking.current) return;
      isTracking.current = false;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX.current;
      const deltaY = Math.abs(touch.clientY - startY.current);

      if (deltaY > Math.abs(deltaX)) return;

      if (!isOpenRef.current && deltaX > minSwipeDistance && startX.current < edgeThreshold) {
        onOpen();
      } else if (isOpenRef.current && deltaX < -minSwipeDistance) {
        onClose();
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, onOpen, onClose, edgeThreshold, minSwipeDistance]);
}
